import {
    Divider,
    mergeClasses,
    Text,
    Tooltip,
} from '@fluentui/react-components';
import {
    trendSubclasses,
    useMetricsValueTileStyles,
} from './NumericalTile.styles';
import { useMemo } from 'react';
import {
    ArrowCircleUpRegular,
    ArrowCircleDownRegular,
    SubtractCircleRegular,
    QuestionCircleRegular,
} from '@fluentui/react-icons';
import MetricsTile, { MetricsTileComponent } from '../MetricsTile/MetricsTile';

export type ValueSize = 'normal' | 'small';
export type TrendDirection = 'const' | 'up' | 'down';

export interface IValueTrend {
    value?: string;
    direction: TrendDirection;
}

interface IValueBase {
    label?: string;
    labelTooltip?: string;
    trend?: IValueTrend;
    size?: ValueSize;
}

export interface INumericalValue extends IValueBase {
    previousValue: string;
    currentValue: string;
    previousTooltip?: string;
    currentTooltip?: string;
}

export interface INumericalTileProps {
    values: INumericalValue[];
    reverseTrend?: boolean;
}

interface IValueEntry extends IValueBase {
    value: string;
    valueTooltip?: string;
}

const NumericalValue: React.FC<{
    entry: IValueEntry;
    reverseTrend?: boolean;
}> = ({ entry, reverseTrend }) => {
    const styles = useMetricsValueTileStyles();

    const { value, trend } = entry;

    const trendElement = useMemo(() => {
        if (!trend) {
            return undefined;
        }

        switch (trend.direction) {
            case 'const':
                return (
                    <div className={styles.trend}>
                        <SubtractCircleRegular />
                        {trend.value}
                    </div>
                );
            case 'up':
                return (
                    <div
                        className={mergeClasses(
                            styles.trend,
                            reverseTrend
                                ? trendSubclasses.negative
                                : trendSubclasses.positive
                        )}
                    >
                        <ArrowCircleUpRegular />
                        {trend.value}
                    </div>
                );
            case 'down':
                return (
                    <div
                        className={mergeClasses(
                            styles.trend,
                            reverseTrend
                                ? trendSubclasses.positive
                                : trendSubclasses.negative
                        )}
                    >
                        <ArrowCircleDownRegular />
                        {trend.value}
                    </div>
                );
        }
    }, [trend, reverseTrend, styles]);

    const textSize = entry.size === 'normal' ? 700 : 500;

    const textComponent = (
        <Text size={textSize} weight="bold">
            {value}
        </Text>
    );

    return (
        <div className={styles.number}>
            {entry.valueTooltip ? (
                <Tooltip
                    content={entry.valueTooltip}
                    relationship="description"
                >
                    {textComponent}
                </Tooltip>
            ) : (
                textComponent
            )}
            {trendElement && trendElement}
        </div>
    );
};

const Column: React.FC<{
    entries: IValueEntry[];
    reverseTrend?: boolean;
}> = ({ entries, reverseTrend }) => {
    const styles = useMetricsValueTileStyles();

    return (
        <>
            {entries.map((e, i) => {
                return (
                    <div key={i} className={styles.value}>
                        {i > 0 && <Divider />}
                        {e.labelTooltip ? (
                            <Tooltip
                                relationship="description"
                                content={e.labelTooltip}
                            >
                                <Text className={styles.labelWithTooltip}>
                                    {e.label} <QuestionCircleRegular />
                                </Text>
                            </Tooltip>
                        ) : (
                            <Text>{e.label}</Text>
                        )}
                        <NumericalValue entry={e} reverseTrend={reverseTrend} />
                    </div>
                );
            })}
        </>
    );
};

const NumericalValueTile: MetricsTileComponent<INumericalTileProps> = ({
    title,
    titleActions,
    values,
    reverseTrend,
    className,
}) => {
    const entries = useMemo<{
        previous: IValueEntry[];
        current: IValueEntry[];
    }>(() => {
        if (!values) {
            return {
                previous: [],
                current: [],
            };
        }

        const previous = new Array<IValueEntry>(values.length);
        const current = new Array<IValueEntry>(values.length);

        for (let i = 0; i < values.length; i++) {
            const value = values[i];

            previous[i] = {
                value: value.previousValue,
                valueTooltip: value.previousTooltip,
                ...value,
                trend: undefined,
            };

            current[i] = {
                value: value.currentValue,
                valueTooltip: value.currentTooltip,
                ...value,
            };
        }

        return {
            previous,
            current,
        };
    }, [values]);

    return (
        <MetricsTile
            title={title}
            titleActions={titleActions}
            previousRangeItem={
                <Column
                    entries={entries.previous}
                    reverseTrend={reverseTrend}
                />
            }
            currentRangeItem={
                <Column entries={entries.current} reverseTrend={reverseTrend} />
            }
            className={className}
        />
    );
};

export default NumericalValueTile;
